import React, { useState } from 'react';
import { Camera, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { uploadFile } from '../../../../../../utils/fileHelpers';
import { logError, logInfo } from '../../../../../../utils/logger';
import { Toast } from '../../../../../../components/common/Toast';

export const NotesAndPhotos = ({ notes, setNotes, photos, setPhotos, loading, surveyId }) => {
  const [toast, setToast] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (event) => {
    event.preventDefault();
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    try {
      // Track successful uploads
      const successfulUploads = [];
      let hasErrors = false;

      for (const file of files) {
        try {
          const url = await uploadFile(file, 'high-pole-photos', surveyId);
          successfulUploads.push(url);
          logInfo(`Photo uploaded successfully: ${url}`);
        } catch (error) {
          logError(`Failed to upload photo: ${file.name}`, error);
          hasErrors = true;
          setToast({
            type: 'error',
            message: `Failed to upload ${file.name}${error.message ? `: ${error.message}` : ''}`
          });
          continue;
        }
      }

      // Update state with successful uploads
      if (successfulUploads.length > 0) {
        setPhotos(prev => [...prev, ...successfulUploads]);
        if (hasErrors) {
          setToast({
            type: 'warning',
            message: 'Some photos failed to upload'
          });
        } else {
          setToast({
            type: 'success',
            message: 'Photos uploaded successfully'
          });
        }
      }
    } catch (error) {
      logError('Photo upload failed:', error);
      setToast({
        type: 'error',
        message: 'Failed to upload photos'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Notes */}
      <div>
        <label className="block text-gray-400 mb-2">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white h-24 resize-none"
          placeholder="Add any additional notes..."
        />
      </div>

      {/* Photos */}
      <div>
        <label className="block text-gray-400 mb-2">Photos</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.capture = 'environment';
              input.onchange = handlePhotoUpload;
              input.click();
            }}
            className="aspect-square flex flex-col items-center justify-center bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600"
            disabled={uploading || loading}
          >
            <Camera className="w-8 h-8 text-gray-400" />
            <span className="text-sm text-gray-400">Take Photo</span>
          </button>
          <label className="aspect-square flex flex-col items-center justify-center bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600">
            <Plus className="w-8 h-8 text-gray-400" />
            <span className="text-sm text-gray-400">Add Photo</span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handlePhotoUpload}
              disabled={uploading || loading}
            />
          </label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mt-2">
          {photos.map((photo, index) => (
            <div key={index} className="relative">
              <img
                src={photo}
                alt={`Photo ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg"
              />
              <button
                onClick={() => setPhotos(photos.filter((_, i) => i !== index))}
                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {uploading && (
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-400">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span>Uploading photos...</span>
          </div>
        )}
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};